import React, { useState, useEffect } from 'react';
import { format, formatDistanceToNow, isPast, addSeconds } from 'date-fns';
import { FiAlertTriangle, FiInfo, FiAlertCircle, FiCheckCircle, FiBell } from 'react-icons/fi';

const AlertsList = ({ alerts, compact = false }) => {
  // Track new alerts for highlighting
  const [newAlerts, setNewAlerts] = useState({});
  
  // When alerts change, highlight new ones
  useEffect(() => {
    if (!alerts || alerts.length === 0) return;
    
    // Get most recent alert
    const latestAlert = alerts[0];
    if (!latestAlert || !latestAlert.id) return;
    
    // Check if it's already in our newAlerts state
    if (newAlerts[latestAlert.id]) return;
    
    // Add to new alerts with an expiration time (30 seconds)
    const expirationTime = addSeconds(new Date(), 30).toISOString();
    setNewAlerts(prev => ({
      ...prev,
      [latestAlert.id]: expirationTime
    }));
    
    // Clean up expired alerts every minute
    const cleanup = () => {
      const now = new Date();
      setNewAlerts(prev => {
        const updated = { ...prev };
        Object.entries(updated).forEach(([id, expTime]) => {
          if (isPast(new Date(expTime))) {
            delete updated[id];
          }
        });
        return updated;
      });
    };
    
    const interval = setInterval(cleanup, 10000);
    return () => clearInterval(interval);
  }, [alerts, newAlerts]);
  
  // If no alerts
  if (!alerts || alerts.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <p>No alerts to display</p>
      </div>
    );
  }
  
  // Get severity icon
  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <FiAlertCircle className="text-ui-error" />;
      case 'error':
        return <FiAlertTriangle className="text-ui-error" />;
      case 'warning':
        return <FiAlertTriangle className="text-ui-warning" />;
      case 'info':
      default:
        return <FiInfo className="text-ui-info" />;
    }
  };
  
  // Get formatted time
  const getFormattedTime = (timestamp) => {
    try {
      // Check if timestamp is valid
      if (!timestamp) {
        console.warn('Invalid timestamp provided to getFormattedTime:', timestamp);
        return {
          relative: 'Unknown time',
          absolute: 'Unknown date'
        };
      }
      
      const date = new Date(timestamp);
      
      // Check if date is valid by ensuring it's not NaN
      if (isNaN(date.getTime())) {
        console.warn('Invalid date created from timestamp:', timestamp);
        return {
          relative: 'Unknown time',
          absolute: 'Unknown date'
        };
      }
      
      return {
        relative: formatDistanceToNow(date, { addSuffix: true }),
        absolute: format(date, 'MMM d, yyyy h:mm a')
      };
    } catch (error) {
      console.error('Error formatting time:', error, timestamp);
      return {
        relative: 'Unknown time',
        absolute: 'Unknown date'
      };
    }
  };
  
  return (
    <div className={`space-y-${compact ? '2' : '4'}`}>
      {alerts.map(alert => {
        const time = getFormattedTime(alert.createdAt);
        const isNew = newAlerts[alert.id] ? true : false;
        
        return (
          <div 
            key={alert.id} 
            className={`flex ${compact ? 'space-x-2' : 'space-x-4'} ${isNew ? 'bg-yellow-50 animate-pulse border border-yellow-200 rounded p-2' : ''}`}
          >
            {/* Alert icon */}
            <div className="flex-shrink-0 mt-1">
              {isNew ? (
                <FiBell className="text-yellow-500 animate-pulse" />
              ) : (
                getSeverityIcon(alert.severity)
              )}
            </div>
            
            {/* Alert content */}
            <div className="flex-1 min-w-0">
              <p className={`${isNew ? 'text-yellow-700 font-bold' : 'text-gray-900'} ${compact ? 'text-sm font-medium' : 'font-semibold'}`}>
                {alert.message}
                {isNew && <span className="ml-2 text-xs bg-yellow-200 text-yellow-800 px-1 rounded">NEW</span>}
              </p>
              
              <div className="flex flex-wrap items-center mt-1">
                {/* Vehicle info */}
                {alert.vehicle && (
                  <span className="text-xs text-primary mr-2">
                    {alert.vehicle.name}
                  </span>
                )}
                
                {/* Geofence info (if available) */}
                {alert.geofence && (
                  <span className="text-xs text-gray-500 mr-2">
                    {alert.geofence.name}
                  </span>
                )}
                
                {/* Timestamp */}
                <span 
                  className="text-xs text-gray-500" 
                  title={time.absolute}
                >
                  {time.relative}
                </span>
              </div>
            </div>
            
            {/* Status/acknowledge button */}
            {!compact && (
              <div className="flex-shrink-0">
                {alert.acknowledged ? (
                  <span className="flex items-center text-ui-success text-sm">
                    <FiCheckCircle className="mr-1" />
                    Acknowledged
                  </span>
                ) : (
                  <button className="text-sm text-secondary hover:text-secondary-dark">
                    Acknowledge
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
      
      {/* View all link */}
      {compact && alerts.length > 0 && (
        <div className="text-center pt-2">
          <a href="/alerts" className="text-sm text-secondary hover:text-secondary-dark">
            View all alerts
          </a>
        </div>
      )}
    </div>
  );
};

export default AlertsList;